import { useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import { Toaster } from "sonner";
import "@/App.css";
import { Nav } from "@/components/Nav";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { PawTrail } from "@/components/Paws";
import { Story } from "@/components/Story";
import { Cattery } from "@/components/Cattery";
import { Care } from "@/components/Care";
import { Pricing } from "@/components/Pricing";
import { Gallery } from "@/components/Gallery";
import { Reviews } from "@/components/Reviews";
import { Contact } from "@/components/Contact";
import { Footer } from "@/components/Footer";
import { MobileBookBar } from "@/components/MobileBookBar";
import { InstagramConnect } from "@/components/InstagramConnect";
import { TermsPage } from "@/components/TermsPage";

const TERMS_HASH = "#/terms-and-conditions";

function App() {
    const [showTerms, setShowTerms] = useState(
        () => window.location.hash === TERMS_HASH
    );
    const lenisRef = useRef(null);
    const firstViewChange = useRef(true);

    useEffect(() => {
        const onHash = () =>
            setShowTerms(window.location.hash === TERMS_HASH);
        window.addEventListener("hashchange", onHash);
        return () => window.removeEventListener("hashchange", onHash);
    }, []);

    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            return undefined;
        }
        const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
        lenisRef.current = lenis;
        let raf;
        const loop = (time) => {
            lenis.raf(time);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);

        const onClick = (e) => {
            const anchor = e.target.closest('a[href^="#"]');
            if (!anchor) return;
            const hash = anchor.getAttribute("href");
            if (
                hash.length > 1 &&
                !hash.startsWith("#/") &&
                document.querySelector(hash)
            ) {
                e.preventDefault();
                lenis.scrollTo(hash, { offset: -70, duration: 1.4 });
            }
        };
        document.addEventListener("click", onClick);

        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
            lenisRef.current = null;
            document.removeEventListener("click", onClick);
        };
    }, []);

    // Switching between the homepage and the Terms & Conditions page:
    // land at the top, or on the requested homepage section.
    useEffect(() => {
        const lenis = lenisRef.current;
        const toTop = () =>
            lenis ? lenis.scrollTo(0, { immediate: true }) : window.scrollTo(0, 0);
        if (firstViewChange.current) {
            firstViewChange.current = false;
            if (!showTerms) return; // normal homepage load — leave scroll alone
        }
        if (showTerms) {
            toTop();
            return;
        }
        const hash = window.location.hash;
        const target =
            hash.length > 1 && !hash.startsWith("#/")
                ? document.querySelector(hash)
                : null;
        if (!target) {
            toTop();
            return;
        }
        const t = setTimeout(
            () =>
                lenis
                    ? lenis.scrollTo(hash, { offset: -70, duration: 1 })
                    : target.scrollIntoView(),
            80
        );
        return () => clearTimeout(t);
    }, [showTerms]);

    return (
        <div className="App grain">
            <Nav />
            {showTerms ? (
                <TermsPage />
            ) : (
                <main>
                    <Hero />
                    <Marquee />
                    <Story />
                    <PawTrail className="bg-cream text-fern/25" />
                    <Cattery />
                    <Care />
                    <PawTrail className="bg-white text-fern/25" />
                    <Pricing />
                    <Gallery />
                    <PawTrail className="bg-cream text-fern/25" />
                    <Reviews />
                    <Contact />
                </main>
            )}
            <Footer />
            {!showTerms && <MobileBookBar />}
            <InstagramConnect />
            <Toaster
                position="top-center"
                toastOptions={{
                    style: {
                        background: "#1A1918",
                        color: "#FAF8F5",
                        border: "1px solid rgba(255,255,255,0.12)",
                    },
                }}
            />
        </div>
    );
}

export default App;
